import FieldLabel from '@components/Common/FieldLabel';
import type { AutocompleteChangeReason, TextFieldProps } from '@mui/material';
import { Autocomplete, TextField, Typography } from '@mui/material';
import React from 'react';
import './index.scss';

interface AutocompleteFormFieldProps {
  // Basic form props
  name: string;
  label: string;
  value?: any;
  inputValue?: string;
  // MUI calls onChange with (event, value, reason, details). `reason` lets callers tell a
  // selectOption from a createOption; onInputChange below already declared its own reason.
  onChange?: (event: any, newValue: any, reason?: AutocompleteChangeReason) => void;
  onInputChange?: (event: any, newInputValue: string, reason?: string) => void;

  // Autocomplete specific props
  options: any[];
  getOptionLabel?: (option: any) => string;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  freeSolo?: boolean;
  multiple?: boolean;
  loading?: boolean;

  // Validation & state
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  placeholder?: string;

  // Auto-validation support (if formData and errors are provided)
  formData?: Record<string, any>;
  errors?: Record<string, string>;

  // Custom styling
  className?: string;
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  fullWidth?: boolean;

  // Additional TextField props passthrough for renderInput
  textFieldProps?: Partial<TextFieldProps>;

  // Additional Autocomplete props
  autocompleteProps?: any;
}

const AutocompleteFormField: React.FC<AutocompleteFormFieldProps> = ({
  name,
  label,
  value,
  inputValue,
  onChange,
  onInputChange,
  options = [],
  getOptionLabel,
  isOptionEqualToValue,
  freeSolo = false,
  multiple = false,
  loading = false,
  required = false,
  disabled = false,
  error,
  helperText,
  placeholder,
  formData,
  errors,
  className = 'form-field',
  variant = 'outlined',
  size = 'medium',
  fullWidth = true,
  textFieldProps = {},
  autocompleteProps = {},
}) => {
  /*
   * A controlled `inputValue` with no working `onInputChange` is a DEAD input, and it looks
   * completely normal in review. MUI's `useControlled` latches on the prop being *defined* — an
   * empty string counts — so the field stays pinned to that value for the component's lifetime:
   * every keystroke is discarded, and because the filter always runs against the pinned string
   * the dropdown shows the whole list instead of narrowing. `/jobs/new` shipped two fields like
   * this (Skills and Tags), where the only way to add a value was to click an option.
   *
   * A genuine handler takes (event, value); a stub takes none, so arity separates them.
   */
  if (process.env.NODE_ENV !== 'production' && inputValue !== undefined) {
    if (!onInputChange || onInputChange.length < 2) {
      // eslint-disable-next-line no-console
      console.error(
        `AutocompleteFormField "${name}": \`inputValue\` is controlled but \`onInputChange\` is ` +
        `missing or a stub, so typing into this field does nothing. Bind inputValue to state and ` +
        `have onInputChange set it — see Companies/CompanyCreatePopup "Add Skill".`,
      );
    }
  }

  // Auto-validation: if formData and errors are provided, use them
  const autoError = formData && errors ? !!errors[name] : error;
  const autoHelperText = formData && errors ? errors[name] : helperText;
  const autoValue = formData ? formData[name] : value;

  return (
    <div className="autocomplete-form-field-container">
      {label && (
        <FieldLabel required={required} disabled={disabled} htmlFor={name}>{label}</FieldLabel>
      )}
      {disabled ? (
        <Typography
          className="disabled-view-typography"
          variant="body1"
        >
          {(() => {
            if (multiple && Array.isArray(autoValue)) {
              return autoValue.length > 0
                ? autoValue.map(item => getOptionLabel ? getOptionLabel(item) : item).join(', ')
                : '-';
            }
            return getOptionLabel && autoValue
              ? getOptionLabel(autoValue)
              : (inputValue || autoValue || '-');
          })()}
        </Typography>
      ) : (
        <Autocomplete
          className={className}
          disabled={disabled}
          freeSolo={freeSolo}
          id={name}
          fullWidth={fullWidth}
          getOptionLabel={getOptionLabel}
          inputValue={inputValue}
          isOptionEqualToValue={isOptionEqualToValue}
          loading={loading}
          multiple={multiple}
          options={options}
          value={autoValue}
          onChange={onChange}
          onInputChange={onInputChange}
          {...autocompleteProps}
          renderInput={(params) => {
            // MUI v9: renderInput params expose slotProps (input / htmlInput / inputLabel), not
            // the removed InputProps. Passing InputProps here forwarded it to the DOM (React
            // logged an unknown-prop warning) and silently dropped the padding override below.
            const { sx: tfSx, ...restTfProps } = textFieldProps;
            return (
              <TextField
                {...params}
                error={autoError}
                helperText={autoHelperText}
                placeholder={placeholder}
                required={required}
                size={size}
                variant={variant}
                // Deliberately NOT copying params.slotProps.input.className here. That value is
                // `MuiAutocomplete-inputRoot`, which belongs on the input root — and the slotProps
                // below already keep it there. Copying it onto the TextField root as well made
                // index.scss's `.MuiAutocomplete-inputRoot { padding-right: 0 !important }` match
                // the FormControl too, cancelling the 32px right gutter that keeps text clear of
                // the popup and clear icons.
                className={`compact-input ${multiple ? '' : 'cc-ac-single'}`}
                sx={{
                  ...(!multiple ? {
                    // The input's own padding and size come from theme.ts MuiAutocomplete; an sx
                    // here loses to MUI's internal rule on order, which is how this field drifted.
                    '& .MuiFormHelperText-root': { marginLeft: 0, fontSize: 'var(--font-size-2xs)' },
                  } : {}),
                  ...tfSx,
                }}
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps?.input,
                    /*
                     * Single-value only, and this is the fix for a real defect.
                     *
                     * `padding: 0` inline zeroes the input root on every side, including the right
                     * gutter MUI reserves for the popup and clear icons -- the same gutter the note
                     * above calls the thing that "keeps text clear" of them. On a single-value
                     * field the input fills the row, so the icons overlap only the tail of a long
                     * value. On a MULTIPLE field the chips take the row and the input shrinks to
                     * what is left: the languages field on a candidate with two languages measured
                     * a 102px input under a 60px adornment, so 59% of the text box, including the
                     * point you would naturally click, was covered.
                     *
                     * Inline rather than a stylesheet is why scoping the SCSS rule alone changed
                     * nothing: an inline declaration beats an ordinary author rule, so the gutter
                     * has to be handled here as well.
                     *
                     * Only the RIGHT side is spared, and that matters. Leaving MUI's padding alone
                     * wholesale put 6px back on the left too, which moved the placeholder in from
                     * 8px to 14px and broke the filter-inset test that exists precisely because
                     * these fields had drifted into three different insets once before. The left,
                     * top and bottom still come from this component; only the icons' clearance is
                     * MUI's.
                     */
                    ...(multiple
                      ? { style: { paddingLeft: 0, paddingTop: 0, paddingBottom: 0 } }
                      : { style: { padding: '0' } }),
                  },
                }}
                {...restTfProps}
              />
            );
          }}
        />
      )}
    </div>
  );
};

export default AutocompleteFormField;
